/**
 * /bookingflight/passenger — Passenger Details (Phase 7).
 *
 * Renders one form per traveller (adults/children/infants derived from the search
 * criteria), collecting name, gender, DOB, nationality (Countries API), and passport
 * details, plus shared contact info. Validates per-field — including age-vs-type and
 * passport-expiry-vs-travel-date rules — then stores everything in BookingContext and
 * advances to the review step.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getCountries } from "../../mock/api";
import { useBooking } from "../../context/BookingContext";
import FlightMiniSummary from "../../components/booking/FlightMiniSummary";
import "./passenger-details.css";

const GENDERS = ["Male", "Female", "Other"];

const TYPE_META = {
  adult: { label: "Adult", hint: "12+ years", icon: "fas fa-user" },
  child: { label: "Child", hint: "2–11 years", icon: "fas fa-child" },
  infant: { label: "Infant", hint: "Under 2 years", icon: "fas fa-baby" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

/** Age in whole years at a reference date. */
const ageAt = (dobISO, refISO) => {
  const dob = new Date(dobISO);
  const ref = new Date(refISO);
  let age = ref.getFullYear() - dob.getFullYear();
  const m = ref.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < dob.getDate())) age--;
  return age;
};

const emptyPassenger = (type) => ({
  type,
  fullName: "",
  gender: "",
  dateOfBirth: "",
  nationality: "",
  passportNumber: "",
  passportExpiry: "",
});

/** Build the passenger slot list from the search criteria counts. */
const buildSlots = (criteria) => {
  const adults = Math.max(1, criteria?.adults || 1);
  const children = criteria?.children || 0;
  const infants = criteria?.infants || 0;
  return [
    ...Array.from({ length: adults }, () => emptyPassenger("adult")),
    ...Array.from({ length: children }, () => emptyPassenger("child")),
    ...Array.from({ length: infants }, () => emptyPassenger("infant")),
  ];
};

export default function PassengerDetails() {
  const navigate = useNavigate();
  const { searchCriteria, selectedFlight, passengers, contact, setPassengers, setContact } = useBooking();

  const [countries, setCountries] = useState([]);
  const [countryStatus, setCountryStatus] = useState("loading");

  const [forms, setForms] = useState(() =>
    passengers && passengers.length ? passengers : buildSlots(searchCriteria)
  );
  const [contactForm, setContactForm] = useState(contact || { email: "", phone: "" });
  const [errors, setErrors] = useState({});

  const departureDate = searchCriteria?.departureDate || todayISO();

  const loadCountries = () => {
    setCountryStatus("loading");
    getCountries()
      .then((list) => {
        setCountries(list);
        setCountryStatus("ready");
      })
      .catch(() => setCountryStatus("error"));
  };

  useEffect(() => {
    loadCountries();
  }, []);

  const setField = (index, key, value) => {
    setForms((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)));
    setErrors((e) => ({ ...e, [`${index}.${key}`]: undefined }));
  };

  const setContactField = (key, value) => {
    setContactForm((prev) => ({ ...prev, [key]: value }));
    setErrors((e) => ({ ...e, [`contact.${key}`]: undefined }));
  };

  const validate = () => {
    const next = {};

    forms.forEach((p, i) => {
      if (!p.fullName.trim()) next[`${i}.fullName`] = "Full name is required.";
      if (!p.gender) next[`${i}.gender`] = "Select a gender.";
      if (!p.nationality) next[`${i}.nationality`] = "Select a nationality.";
      if (!p.passportNumber.trim()) next[`${i}.passportNumber`] = "Passport number is required.";

      if (!p.dateOfBirth) {
        next[`${i}.dateOfBirth`] = "Date of birth is required.";
      } else {
        const age = ageAt(p.dateOfBirth, departureDate);
        if (age < 0) next[`${i}.dateOfBirth`] = "Date of birth can't be in the future.";
        else if (p.type === "adult" && age < 12) next[`${i}.dateOfBirth`] = "Adults must be 12+ on the travel date.";
        else if (p.type === "child" && (age < 2 || age >= 12)) next[`${i}.dateOfBirth`] = "Children must be 2–11 on the travel date.";
        else if (p.type === "infant" && age >= 2) next[`${i}.dateOfBirth`] = "Infants must be under 2 on the travel date.";
      }

      if (!p.passportExpiry) {
        next[`${i}.passportExpiry`] = "Passport expiry is required.";
      } else if (p.passportExpiry <= departureDate) {
        next[`${i}.passportExpiry`] = "Passport must be valid past the travel date.";
      }
    });

    if (!contactForm.email.trim()) next["contact.email"] = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) next["contact.email"] = "Enter a valid email.";
    if (!contactForm.phone.trim()) next["contact.phone"] = "Phone number is required.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setPassengers(forms);
    setContact(contactForm);
    toast.success("Your request is submitted");
    navigate("/");
  };

  const counts = useMemo(() => {
    const c = { adult: 0, child: 0, infant: 0 };
    forms.forEach((p) => (c[p.type] += 1));
    return c;
  }, [forms]);

  return (
    <section className="pd-page">
      <div className="pd-header">
        <div className="pd-header__inner">
          <button type="button" className="pd-back" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back to results
          </button>
          <h1 className="pd-title">Passenger Details</h1>
          <p className="pd-subtitle">
            {counts.adult} Adult{counts.adult === 1 ? "" : "s"}
            {counts.child ? `, ${counts.child} Child${counts.child === 1 ? "" : "ren"}` : ""}
            {counts.infant ? `, ${counts.infant} Infant${counts.infant === 1 ? "" : "s"}` : ""}
          </p>
        </div>
      </div>

      <div className="pd-body">
        <form className="pd-main" onSubmit={handleSubmit} noValidate>
          {forms.map((p, i) => {
            const meta = TYPE_META[p.type];
            return (
              <div className="pd-card" key={i}>
                <div className="pd-card__head">
                  <span className="pd-card__icon"><i className={meta.icon} aria-hidden="true" /></span>
                  <div>
                    <h3 className="pd-card__title">
                      {meta.label} {p.type === "adult" ? i + 1 : ""}
                    </h3>
                    <span className="pd-card__hint">{meta.hint}</span>
                  </div>
                </div>

                <div className="pd-grid">
                  <div className="pd-field pd-field--wide">
                    <label>Full Name (as on passport)</label>
                    <input
                      type="text"
                      value={p.fullName}
                      onChange={(e) => setField(i, "fullName", e.target.value)}
                      placeholder="e.g. Ahmed Ali Khan"
                      className={errors[`${i}.fullName`] ? "pd-input pd-input--error" : "pd-input"}
                    />
                    {errors[`${i}.fullName`] && <span className="pd-error">{errors[`${i}.fullName`]}</span>}
                  </div>

                  <div className="pd-field">
                    <label>Gender</label>
                    <select
                      value={p.gender}
                      onChange={(e) => setField(i, "gender", e.target.value)}
                      className={errors[`${i}.gender`] ? "pd-input pd-input--error" : "pd-input"}
                    >
                      <option value="">Select</option>
                      {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    {errors[`${i}.gender`] && <span className="pd-error">{errors[`${i}.gender`]}</span>}
                  </div>

                  <div className="pd-field">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={p.dateOfBirth}
                      max={todayISO()}
                      onChange={(e) => setField(i, "dateOfBirth", e.target.value)}
                      className={errors[`${i}.dateOfBirth`] ? "pd-input pd-input--error" : "pd-input"}
                    />
                    {errors[`${i}.dateOfBirth`] && <span className="pd-error">{errors[`${i}.dateOfBirth`]}</span>}
                  </div>

                  <div className="pd-field">
                    <label>Nationality</label>
                    {countryStatus === "loading" && <div className="pd-input pd-input--loading">Loading…</div>}
                    {countryStatus === "error" && (
                      <button type="button" className="pd-input pd-input--retry" onClick={loadCountries}>
                        Retry loading countries
                      </button>
                    )}
                    {countryStatus === "ready" && (
                      <select
                        value={p.nationality}
                        onChange={(e) => setField(i, "nationality", e.target.value)}
                        className={errors[`${i}.nationality`] ? "pd-input pd-input--error" : "pd-input"}
                      >
                        <option value="">Select country</option>
                        {countries.map((c) => (
                          <option key={c.id} value={c.country}>{c.country}</option>
                        ))}
                      </select>
                    )}
                    {errors[`${i}.nationality`] && <span className="pd-error">{errors[`${i}.nationality`]}</span>}
                  </div>

                  <div className="pd-field">
                    <label>Passport Number</label>
                    <input
                      type="text"
                      value={p.passportNumber}
                      onChange={(e) => setField(i, "passportNumber", e.target.value.toUpperCase())}
                      placeholder="e.g. AB1234567"
                      className={errors[`${i}.passportNumber`] ? "pd-input pd-input--error" : "pd-input"}
                    />
                    {errors[`${i}.passportNumber`] && <span className="pd-error">{errors[`${i}.passportNumber`]}</span>}
                  </div>

                  <div className="pd-field">
                    <label>Passport Expiry</label>
                    <input
                      type="date"
                      value={p.passportExpiry}
                      min={todayISO()}
                      onChange={(e) => setField(i, "passportExpiry", e.target.value)}
                      className={errors[`${i}.passportExpiry`] ? "pd-input pd-input--error" : "pd-input"}
                    />
                    {errors[`${i}.passportExpiry`] && <span className="pd-error">{errors[`${i}.passportExpiry`]}</span>}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Contact */}
          <div className="pd-card">
            <div className="pd-card__head">
              <span className="pd-card__icon"><i className="fas fa-address-book" aria-hidden="true" /></span>
              <div>
                <h3 className="pd-card__title">Contact Information</h3>
                <span className="pd-card__hint">Booking updates are sent here</span>
              </div>
            </div>
            <div className="pd-grid">
              <div className="pd-field">
                <label>Email Address</label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactField("email", e.target.value)}
                  placeholder="you@example.com"
                  className={errors["contact.email"] ? "pd-input pd-input--error" : "pd-input"}
                />
                {errors["contact.email"] && <span className="pd-error">{errors["contact.email"]}</span>}
              </div>
              <div className="pd-field">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={contactForm.phone}
                  onChange={(e) => setContactField("phone", e.target.value.replace(/[^\d+\s-]/g, ""))}
                  placeholder="+92 300 1234567"
                  className={errors["contact.phone"] ? "pd-input pd-input--error" : "pd-input"}
                />
                {errors["contact.phone"] && <span className="pd-error">{errors["contact.phone"]}</span>}
              </div>
            </div>
          </div>

          <div className="pd-actions">
            <button type="submit" className="pd-continue">
              Submit Query <i className="fas fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </form>

        {/* Aside */}
        <aside className="pd-aside">
          <FlightMiniSummary flight={selectedFlight} criteria={searchCriteria} />
        </aside>
      </div>
    </section>
  );
}
